import { FC } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateEmail } from '../../../modules/auth/_requests';
import { useAuth } from '../../../modules/auth/AuthContext';

const EmailForm: FC = () => {
  const { t } = useTranslation('security');
  const { currentUser, setCurrentUser } = useAuth();

  const schema = Yup.object().shape({
    newEmail: Yup.string()
      .email(t('newEmail.invalid'))
      .required(t('newEmail.required')),
    confirmPassword: Yup.string().required(t('currentPassword.required')),
  });

  const formik = useFormik({
    initialValues: { newEmail: '', confirmPassword: '' },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        await toast.promise(
          updateEmail(values).then((result) => {
            if (result) {
              setCurrentUser((prev) =>
                prev ? { ...prev, email: values.newEmail } : undefined
              );

              return true;
            }

            return false;
          }),
          {
            pending: t('message.pending'),
            success: t('message.email.success'),
            error: t('message.email.error'),
          }
        );
      } catch (ex) {
        console.error(ex);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit} className="w-100">
      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">{t('currentEmail')}</Form.Label>
        <p>{currentUser?.email}</p>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">
          {t('newEmail.label')}
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={t('newEmail.placeholder')}
          {...formik.getFieldProps('newEmail')}
          autoComplete="email"
          aria-autocomplete="none"
          isInvalid={
            formik.touched.newEmail && formik.errors.newEmail !== undefined
          }
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.newEmail}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">
          {t('currentPassword.label')}
        </Form.Label>
        <Form.Control
          type="password"
          placeholder={t('currentPassword.placeholder')}
          {...formik.getFieldProps('confirmPassword')}
          autoComplete="current-password"
          aria-autocomplete="none"
          isInvalid={
            formik.touched.confirmPassword &&
            formik.errors.confirmPassword !== undefined
          }
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.confirmPassword}
        </Form.Control.Feedback>
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
      >
        {formik.isSubmitting ? (
          <span className="indicator-progress">
            {t('button.wait') + ' '}
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        ) : (
          <span className="indicator-label">{t('button.save')}</span>
        )}
      </Button>
    </Form>
  );
};

export default EmailForm;
